import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Button, message, Space, Typography, Divider } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../config';

const { Text, Title } = Typography;

interface BulkPushRoomTypesModalProps {
  visible: boolean;
  hotelId: number;
  emHotelId: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface EmLanguage {
  id: number;
  code: string;
  label: string;
}

const LANGUAGE_MATCH_MAP: Record<string, string[]> = {
  'en-US': ['english', 'en', 'en-us', 'en-gb'],
  'en-GB': ['english', 'en', 'en-us', 'en-gb'],
  'nl-NL': ['dutch', 'nl', 'nl-nl', 'nederlands'],
  'de-DE': ['german', 'de', 'de-de', 'deutsch'],
  'fr-FR': ['french', 'fr', 'fr-fr', 'français'],
  'es-ES': ['spanish', 'es', 'es-es', 'español'],
  'it-IT': ['italian', 'it', 'it-it', 'italiano'],
};

const BulkPushRoomTypesModal: React.FC<BulkPushRoomTypesModalProps> = ({
  visible,
  hotelId,
  emHotelId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [emLanguages, setEmLanguages] = useState<EmLanguage[]>([]);
  const [loading, setLoading] = useState(false);
  const [roomTypeCount, setRoomTypeCount] = useState(0);
  const [alreadyPushedCount, setAlreadyPushedCount] = useState(0);
  const [roomTypes, setRoomTypes] = useState<any[]>([]);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);

  useEffect(() => {
    if (visible && hotelId !== undefined) {
      loadEmLanguages();
      loadRoomTypeData();
    } else if (!visible) {
      form.resetFields();
      setRoomTypes([]);
      setRoomTypeCount(0);
      setAlreadyPushedCount(0);
      setAvailableLanguages([]);
    }
  }, [visible, hotelId]);

  const loadEmLanguages = async () => {
    try {
      const response = await axios.get(`${API_URL}/em-push/languages`);
      setEmLanguages(response.data);
    } catch (error) {
      console.error('Failed to load EM languages:', error);
      message.error('Failed to load EM languages');
    }
  };

  // Auto-match language mappings when both EM languages and available languages are loaded
  useEffect(() => {
    if (emLanguages.length > 0 && availableLanguages.length > 0) {
      const autoMappings: Record<string, number> = {};
      for (const lang of availableLanguages) {
        const possibleMatches = LANGUAGE_MATCH_MAP[lang] || [lang.split('-')[0], lang];
        const match = emLanguages.find(emLang =>
          possibleMatches.some(m => emLang.code.toLowerCase() === m)
        );
        if (match) {
          autoMappings[`mapping_${lang}`] = match.id;
        }
      }
      if (Object.keys(autoMappings).length > 0) {
        form.setFieldsValue(autoMappings);
      }
    }
  }, [emLanguages, availableLanguages]);

  const loadRoomTypeData = async () => {
    try {
      const response = await axios.get(`${API_URL}/hotels/${hotelId}/room-types`);
      const flattenedData = response.data;

      // Group flattened room types back into unique room types with names objects
      const roomTypeMap = new Map<number, any>();
      const languageCodes = new Set<string>();

      flattenedData.forEach((item: any) => {
        const key = item.dbId || item.id;

        if (!roomTypeMap.has(key)) {
          roomTypeMap.set(key, {
            id: key,
            mewsId: item.categoryId,
            isActive: item.isActive,
            name: item.name,
            emCategoryId: item.emCategoryId,
            emPmsCategoryId: item.emPmsCategoryId,
            names: {},
          });
        }

        const roomType = roomTypeMap.get(key);
        if (item.languageCode) {
          languageCodes.add(item.languageCode);
          if (item.roomType && item.roomType !== '-') {
            roomType.names[item.languageCode] = item.roomType;
          }
        }
      });

      const groupedRoomTypes = Array.from(roomTypeMap.values());
      const alreadyPushed = groupedRoomTypes.filter(
        rt => rt.emCategoryId && rt.emPmsCategoryId
      );

      setRoomTypeCount(groupedRoomTypes.length);
      setAlreadyPushedCount(alreadyPushed.length);
      setRoomTypes(groupedRoomTypes.slice(0, 10));
      setAvailableLanguages(Array.from(languageCodes));
    } catch (error) {
      console.error('Failed to load room types:', error);
      message.error('Failed to load room types');
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const languageMappings = availableLanguages
        .filter(lang => values[`mapping_${lang}`])
        .map(lang => ({
          sqliteLanguage: lang,
          emLanguageId: values[`mapping_${lang}`],
        }));

      if (languageMappings.length === 0) {
        message.warning('Please map at least one language');
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API_URL}/em-push/bulk-room-types`, {
        hotelId,
        emHotelId,
        languageMappings,
      });

      if (response.data.success) {
        message.success(
          `Successfully pushed ${response.data.totalPushed} room types to EM!`
        );
        if (response.data.totalSkipped > 0) {
          message.info(`${response.data.totalSkipped} room types were already pushed (skipped)`);
        }
        if (response.data.totalFailed > 0) {
          message.warning(`${response.data.totalFailed} room types failed to push`);
        }
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error('Bulk push error:', error);
      message.error(error.response?.data?.message || 'Failed to push room types to EM');
    } finally {
      setLoading(false);
    }
  };

  const pendingCount = roomTypeCount - alreadyPushedCount;

  return (
    <Modal
      title="Bulk Push All Room Types to EM"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      style={{ top: 20 }}
      styles={{ body: { padding: '12px 24px 16px' } }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        {/* Summary */}
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f5ff', borderRadius: 6, fontSize: 13 }}>
          <Text strong>Total:</Text> {roomTypeCount} &nbsp;|&nbsp; <Text strong>Pushed:</Text> {alreadyPushedCount} &nbsp;|&nbsp; <Text strong>Pending:</Text>{' '}
          <Text type={pendingCount > 0 ? 'success' : 'secondary'} strong>{pendingCount}</Text>
          &nbsp;|&nbsp; <Text strong>Languages:</Text> {availableLanguages.join(', ')}
        </div>

        {/* Preview */}
        {roomTypes.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ fontSize: 13 }}>Preview ({roomTypes.length} room types)</Text>
            <div style={{
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              padding: '6px 12px',
              maxHeight: 150,
              overflowY: 'auto',
              background: '#fafafa',
              marginTop: 4,
            }}>
              {roomTypes.map((rt, idx) => {
                const rtName = (rt.names && Object.values(rt.names)[0]) || rt.name || 'Unnamed';
                const isPushed = rt.emCategoryId && rt.emPmsCategoryId;
                return (
                  <div key={rt.id} style={{ marginBottom: 2, fontSize: 13 }}>
                    <Text>
                      {idx + 1}. {rtName}
                      {isPushed && (
                        <Text type="secondary" style={{ marginLeft: 6 }}>(already pushed)</Text>
                      )}
                    </Text>
                  </div>
                );
              })}
              {roomTypeCount > 10 && (
                <Text type="secondary" style={{ fontStyle: 'italic', fontSize: 12 }}>
                  ...and {roomTypeCount - 10} more
                </Text>
              )}
            </div>
          </div>
        )}

        <Divider style={{ margin: '12px 0' }} />

        {/* Language Mapping */}
        <Text strong style={{ fontSize: 13 }}>Language Mapping</Text>

        <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 6, marginTop: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{
                  padding: '6px 12px',
                  background: '#f0f5ff',
                  borderBottom: '1px solid #d9d9d9',
                  textAlign: 'left',
                  fontWeight: 600,
                  fontSize: 13,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}>
                  Source Language
                </th>
                <th style={{
                  width: '40px',
                  padding: '6px',
                  background: '#fafafa',
                  borderBottom: '1px solid #d9d9d9',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}></th>
                <th style={{
                  padding: '6px 12px',
                  background: '#f6ffed',
                  borderBottom: '1px solid #d9d9d9',
                  textAlign: 'left',
                  fontWeight: 600,
                  fontSize: 13,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}>
                  EM Language
                </th>
              </tr>
            </thead>
            <tbody>
              {availableLanguages.map((lang, index) => (
                <tr key={lang} style={{ borderBottom: index < availableLanguages.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <td style={{ padding: '6px 12px', background: '#fafafa', verticalAlign: 'middle' }}>
                    <Text strong style={{ color: '#1890ff', fontSize: 13 }}>{lang}</Text>
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', verticalAlign: 'middle' }}>
                    <ArrowRightOutlined style={{ fontSize: 14, color: '#1890ff' }} />
                  </td>
                  <td style={{ padding: '4px 12px', background: '#f6fff6', verticalAlign: 'middle' }}>
                    <Form.Item
                      name={`mapping_${lang}`}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder="Select EM language"
                        allowClear
                        options={emLanguages.map(emLang => ({
                          value: emLang.id,
                          label: `${emLang.code} (ID: ${emLang.id})`,
                        }))}
                      />
                    </Form.Item>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={pendingCount === 0}
            >
              Push {pendingCount} Room Type{pendingCount !== 1 ? 's' : ''} to EM
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BulkPushRoomTypesModal;
